import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheckCircle, faTimesCircle, faAngleUp, faCircleInfo, faLightbulb } from '@fortawesome/free-solid-svg-icons';
import './highlight.css'; // Moved to top as per ESLint rule
import examApi from '../../services/api/examApi';
import { getLinkHref, isQuestionAnswered } from '../../services/hateoasUtils';
import { shouldPreventBackendUpdates } from '../../utils/examEndChecker';
import clsx from 'clsx';
import './CleanAnswerOptions.css';
import AssetDisplay from './AssetDisplay';
import InsightPanel from './InsightPanel';

/**
 * Returns true only when an answer has a non-empty NBME Insight: the `insights`
 * field must be a string whose TEXT content (HTML tags stripped, whitespace
 * trimmed) is non-empty. So `<p></p>` or pure whitespace counts as empty.
 */
const hasInsight = (ans) => {
  const html = ans?.insights;
  if (!html || typeof html !== 'string') return false;
  const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ');
  return text.trim().length > 0;
};

/**
 * Clean AnswerOptions component with CSS class-based styling and proper parent-child state management
 * Uses .option, .option.correct, .option.incorrect CSS classes
 * All state management is handled by parent component
 */
const CleanAnswerOptions = forwardRef(({
  currentQuestion,
  selectedAnswer,
  setSelectedAnswer,
  isAnswerSubmitted, // Controlled by parent
  setIsAnswerSubmitted, // For backward compatibility (not used)
  isCorrect, // Controlled by parent
  setIsCorrect, // For backward compatibility (not used)
  isFlagged,
  themeStyles,
  isDarkMode,
  examData, // Exam data for end date checking
  isLocked = false, // NEW: Disables UI when question is locked (answered/skipped)
  onAnswerSubmitted, // Callback to parent
  onAnswersLoaded, // Callback to sync answers with parent
  onNavigateNext, // Callback to navigate to next question after submission
  onSkipQuestion, // Callback to skip question without marking as answered
  submitTrigger = 0, // NEW: External trigger for submission
  hideButtons = false, // NEW: Hide internal buttons when parent handles them
  reviewMode = false // NEW: When true (finished/closed exam), enable explanations and lock UI
}, ref) => {
  // console.log('[CleanAnswerOptions] Component render with props:', {
  //   questionId: currentQuestion?.id,
  //   selectedAnswer,
  //   isAnswerSubmitted,
  //   isCorrect,
  //   timestamp: new Date().toISOString()
  // });

  // Local state - only for loading and answers data
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [timeSpent, setTimeSpent] = useState(0);
  const [explanationVisibility, setExplanationVisibility] = useState({});
  const [insightVisibility, setInsightVisibility] = useState({});
    // Refs for tracking
  const startTimeRef = useRef(Date.now());
  const processedQuestionsRef = useRef(new Set());
  const previousPropsRef = useRef({});

  // Track prop changes for debugging
  useEffect(() => {
    const currentProps = {
      questionId: currentQuestion?.id,
      selectedAnswer,
      isAnswerSubmitted,
      isCorrect
    };
    
    const previousProps = previousPropsRef.current;
    const changes = {};
    
    Object.keys(currentProps).forEach(key => {
      if (currentProps[key] !== previousProps[key]) {
        changes[key] = {
          from: previousProps[key],
          to: currentProps[key]
        };
      }
    });
    
    if (Object.keys(changes).length > 0) {
      // console.log('[CleanAnswerOptions] Props changed:', {
      //   timestamp: new Date().toISOString(),
      //   changes,
      //   fullCurrentProps: currentProps,
      //   fullPreviousProps: previousProps
      // });
    }
    
    previousPropsRef.current = currentProps;
  }, [currentQuestion?.id, selectedAnswer, isAnswerSubmitted, isCorrect]);

  // Reset when question changes
  useEffect(() => {
    // console.log('[CleanAnswerOptions] Question change effect triggered:', {
    //   questionId: currentQuestion?.id,
    //   previousExplanationVisibility: explanationVisibility
    // });
    
    if (currentQuestion?.id) {
      setExplanationVisibility({});
      setInsightVisibility({});
      startTimeRef.current = Date.now();
      processedQuestionsRef.current.clear();
      
      // console.log('[CleanAnswerOptions] Reset explanation visibility for new question');
    }
  }, [currentQuestion?.id]);

  // Auto-open explanations when answer is submitted or in review mode
  useEffect(() => {
    // console.log('[CleanAnswerOptions] Auto-open explanations effect triggered:', {
    //   isAnswerSubmitted,
    //   selectedAnswer,
    //   answersLength: answers.length,
    //   timestamp: new Date().toISOString()
    // });

  if ((isAnswerSubmitted || reviewMode) && answers.length > 0) {
      // console.log('[CleanAnswerOptions] Processing auto-open explanations...');
      
      // Find the selected answer index and the correct answer index
      const selectedIndex = selectedAnswer ? selectedAnswer.charCodeAt(0) - 65 : -1;
      const correctIndex = answers.findIndex(ans => ans.isCorrect === true);
      
      // console.log('[CleanAnswerOptions] Explanation indices:', {
      //   selectedIndex,
      //   correctIndex,
      //   selectedAnswer,
      //   answers: answers.map((ans, idx) => ({
      //     index: idx,
      //     letter: String.fromCharCode(65 + idx),
      //     isCorrect: ans.isCorrect,
      //     hasExplanation: hasExplanation(ans)
      //   }))
      // });
      
      // Create a new visibility state with those explanations visible
      const newVisibility = {};
      if (selectedIndex >= 0 && hasExplanation(answers[selectedIndex])) {
        newVisibility[selectedIndex] = true;
        // console.log('[CleanAnswerOptions] Auto-opening explanation for selected answer:', selectedIndex);
      }
      if (correctIndex >= 0 && correctIndex !== selectedIndex && hasExplanation(answers[correctIndex])) {
        newVisibility[correctIndex] = true;
        // console.log('[CleanAnswerOptions] Auto-opening explanation for correct answer:', correctIndex);
      }
      
      // console.log('[CleanAnswerOptions] Setting new explanation visibility:', newVisibility);
      // Update the visibility state
      setExplanationVisibility(newVisibility);
    }
  }, [isAnswerSubmitted, selectedAnswer, answers, reviewMode]);
  // Time tracking - optimized to prevent unnecessary re-renders; disabled in review mode
  useEffect(() => {
    // Only track time if not submitted
    if (!isAnswerSubmitted && !reviewMode) {
      const timer = setInterval(() => {
        setTimeSpent(prevTime => {
          const newTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
          // Only update if time actually changed
          return prevTime !== newTime ? newTime : prevTime;
        });
      }, 1000);
      return () => clearInterval(timer);
    }  }, [isAnswerSubmitted, reviewMode]);

  // Expose submitAnswer method to parent via ref
  useImperativeHandle(ref, () => ({
    submitAnswer: () => {
      // console.log('[CleanAnswerOptions] External submitAnswer called via ref');
      submitAnswer();
    }
  }), [selectedAnswer, submitting, isAnswerSubmitted, isLocked]);

  // Watch for external submit trigger
  useEffect(() => {
    if (submitTrigger > 0) {
      // console.log('[CleanAnswerOptions] External submitTrigger received:', submitTrigger);
      if (!isAnswerSubmitted && !submitting && !isLocked && selectedAnswer) {
        // console.log('[CleanAnswerOptions] Triggering submission via submitTrigger');
        submitAnswer();
      } else {
        // console.log('[CleanAnswerOptions] Cannot submit via trigger - invalid state:', {
        //   isAnswerSubmitted,
        //   submitting,
        //   isLocked,
        //   selectedAnswer
        // });
      }
    }
  }, [submitTrigger]);

  // Load answers when question changes
  useEffect(() => {
    const fetchAnswers = async () => {
      // console.log('[CleanAnswerOptions] Fetching answers for question:', currentQuestion?.id);
      
      if (!currentQuestion?.id) {
        // console.log('[CleanAnswerOptions] No question ID, setting loading false');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        let answersData = null;

        // Try to get answers from HATEOAS link
        if (currentQuestion._links?.answers?.href) {
          try {
            // console.log('[CleanAnswerOptions] Fetching answers from HATEOAS link:', currentQuestion._links.answers.href);
            answersData = await examApi.followHateoasLink(currentQuestion._links.answers.href);
            // console.log('[CleanAnswerOptions] HATEOAS answers received:', answersData);
          } catch (err) {
            // console.warn('[CleanAnswerOptions] HATEOAS link failed:', err.message);
          }
        }

        // Fallback to direct API call
        if (!answersData || !Array.isArray(answersData) || answersData.length === 0) {
          const directUrl = `/api/question/${currentQuestion.id}/answers`;
          try {
            // console.log('[CleanAnswerOptions] Fetching answers from direct API:', directUrl);
            answersData = await examApi.followHateoasLink(directUrl);
            // console.log('[CleanAnswerOptions] Direct API answers received:', answersData);
          } catch (err) {
            // console.warn('[CleanAnswerOptions] Direct API call failed:', err.message);
          }
        }

        // Check embedded answers
        if (!answersData || !Array.isArray(answersData) || answersData.length === 0) {
          if (currentQuestion._embedded?.answers) {
            // console.log('[CleanAnswerOptions] Using embedded answers:', currentQuestion._embedded.answers);
            answersData = currentQuestion._embedded.answers;
          }
        }

        if (!answersData || !Array.isArray(answersData) || answersData.length === 0) {
          // console.error('[CleanAnswerOptions] No answer options available');
          setError('No answer options available for this question.');
          setLoading(false);
          return;
        }
        
        // console.log('[CleanAnswerOptions] Setting answers:', {
        //   count: answersData.length,
        //   answers: answersData.map((ans, idx) => ({
        //     index: idx,
        //     id: ans.id,
        //     isCorrect: ans.isCorrect,
        //     hasExplanation: !!(ans.explanation || ans.feedback || ans.explanationFormatted),
        //     body: ans.body?.substring(0, 50) + '...'
        //   }))
        // });
        
        setAnswers(answersData);
        
        // Notify parent component of loaded answers
        if (onAnswersLoaded) {
          // console.log('[CleanAnswerOptions] Notifying parent of loaded answers');
          onAnswersLoaded(answersData);
        }
        
        checkPreviousAnswers(answersData);
      } catch (err) {
        // console.error('[CleanAnswerOptions] Error loading answers:', err);
        setError(`Failed to load answers: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchAnswers();
  }, [currentQuestion]);
  // Check if question was previously answered
  const checkPreviousAnswers = (answersData) => {
    if (!currentQuestion?.response || !answersData) return;

    const questionKey = `${currentQuestion.id}_${currentQuestion.response?.timestamp || 'no_timestamp'}`;
    if (processedQuestionsRef.current.has(questionKey)) return;
    processedQuestionsRef.current.add(questionKey);

    const questionResponse = currentQuestion.response;
    
    if (isQuestionAnswered(questionResponse)) {
      const answeredIds = questionResponse.answerIds || [];
      
      // console.log('[CleanAnswerOptions] Previous answer detected:', {
      //   questionId: currentQuestion?.id,
      //   answeredIds,
      //   isCorrect: questionResponse.isCorrect,
      //   currentSelectedAnswer: selectedAnswer
      // });      // Don't set selectedAnswer here - let the parent handle all state management
      // The parent's hydrateCurrentQuestionState should handle this
      
      // Notify parent of previous answer
      if (onAnswerSubmitted && questionResponse.isCorrect !== undefined) {
        // Create complete response object for previously answered question
        const responseObject = {
          questionId: currentQuestion?.id,
          isCorrect: questionResponse.isCorrect,
          answerIds: answeredIds,
          timeSpent: questionResponse.timeSpent || 0,
          isFlagged: questionResponse.isFlagged || false,
          timestamp: questionResponse.timestamp || new Date().toISOString()
        };
        
        // console.log('[CleanAnswerOptions] CALLING onAnswerSubmitted for previously answered question:', responseObject);
        onAnswerSubmitted(responseObject);
      }
    }
  };  // Handle answer selection
  const handleAnswerSelect = (letter) => {
    if (isAnswerSubmitted || submitting || isLocked || reviewMode) {
      // console.log('[CleanAnswerOptions] Answer selection blocked:', {
      //   isAnswerSubmitted,
      //   submitting,
      //   isLocked,
      //   reviewMode,
      //   attempted: letter
      // });
      return;
    }

    // Check if exam has ended before allowing answer selection
    if (shouldPreventBackendUpdates(examData)) {
      // console.log('[CleanAnswerOptions] Exam has ended or is finished, preventing answer selection');
      return;
    }

    setSelectedAnswer(letter);
  };// Submit answer
  const submitAnswer = async () => {
    if (!selectedAnswer || isAnswerSubmitted || submitting || isLocked) return;

    // Check if exam has ended before allowing answer submissions
    if (shouldPreventBackendUpdates(examData)) {
      // console.log('[CleanAnswerOptions] Exam has ended or is finished, preventing answer submission');
      return;
    }

    // console.log('[CleanAnswerOptions] ======= SUBMIT ANSWER DEBUG =======');
    // console.log('[CleanAnswerOptions] Submit started with:', {
    //   selectedAnswer,
    //   timeSpent,
    //   isFlagged,
    //   questionId: currentQuestion?.id,
    //   timestamp: new Date().toISOString()
    // });

    setSubmitting(true);
    try {
      const selectedIndex = selectedAnswer.charCodeAt(0) - 65;
      const selectedAnswerObj = answers[selectedIndex];
      const isAnswerCorrect = selectedAnswerObj?.isCorrect === true;

      const payload = {
        Answers: [selectedAnswerObj.id],
        TimeSpent: timeSpent,
        IsFlagged: isFlagged || false
      };

      // console.log('[CleanAnswerOptions] Submission payload created:', payload);      // DETAILED LOGGING FOR ENDPOINT DETECTION
      // console.log('[CleanAnswerOptions] ===== ENDPOINT DETECTION DEBUG =====');
      // console.log('[CleanAnswerOptions] Current question object structure:', {
      //   questionId: currentQuestion?.id,
      //   examId: currentQuestion?.examId,
      //   hasResponse: !!currentQuestion?.response,
      //   responseLinks: currentQuestion?.response?._links,
      //   questionLinks: currentQuestion?._links,
      //   fullQuestionKeys: Object.keys(currentQuestion || {}),
      //   questionPreview: JSON.stringify(currentQuestion, null, 2).substring(0, 1000) + '...'
      // });

      // console.log('[CleanAnswerOptions] Available HATEOAS links in question:', {
      //   questionLinksKeys: currentQuestion?._links ? Object.keys(currentQuestion._links) : 'No _links',
      //   questionLinksDetail: currentQuestion?._links ? JSON.stringify(currentQuestion._links, null, 2) : 'No _links available',
      //   responseLinksKeys: currentQuestion?.response?._links ? Object.keys(currentQuestion.response._links) : 'No response._links'
      // });

      // Find submit endpoint
      let submitLink;
      
      // Method 1: Check response._links for 'save'
      if (currentQuestion?.response?._links) {
        // console.log('[CleanAnswerOptions] Method 1: Checking response._links for save:', {
        //   responseLinks: currentQuestion.response._links,
        //   linkKeys: Object.keys(currentQuestion.response._links)
        // });
        
        // Add detailed logging around getLinkHref call
        // console.log('[CleanAnswerOptions] Method 1: Calling getLinkHref with:', {
        //   links: currentQuestion.response._links,
        //   rel: 'save',
        //   linkStructure: JSON.stringify(currentQuestion.response._links, null, 2)
        // });
        submitLink = getLinkHref(currentQuestion.response._links, 'save');
        // console.log('[CleanAnswerOptions] Method 1: getLinkHref result:', { submitLink });
      } else {
        // console.log('[CleanAnswerOptions] Method 1: No response._links available');
      }
      
      // Method 2: Check question._links for 'answer' or 'save'
      if (!submitLink && currentQuestion?._links) {
        // console.log('[CleanAnswerOptions] Method 2: Checking question._links:', {
        //   questionLinks: currentQuestion._links,
        //   linkKeys: Object.keys(currentQuestion._links)
        // });
        
        // Add detailed logging around getLinkHref calls
        // console.log('[CleanAnswerOptions] Method 2a: Calling getLinkHref for answer link:', {
        //   links: currentQuestion._links,
        //   rel: 'answer',
        //   linkStructure: JSON.stringify(currentQuestion._links, null, 2)
        // });
        const answerLink = getLinkHref(currentQuestion._links, 'answer');
        // console.log('[CleanAnswerOptions] Method 2a: getLinkHref result for answer:', { answerLink });
        
        // console.log('[CleanAnswerOptions] Method 2b: Calling getLinkHref for save link:', {
        //   links: currentQuestion._links,
        //   rel: 'save',
        //   linkStructure: JSON.stringify(currentQuestion._links, null, 2)
        // });
        const saveLink = getLinkHref(currentQuestion._links, 'save');
        // console.log('[CleanAnswerOptions] Method 2b: getLinkHref result for save:', { saveLink });
        
        // console.log('[CleanAnswerOptions] Method 2 individual results:', { answerLink, saveLink });
        submitLink = answerLink || saveLink;
        // console.log('[CleanAnswerOptions] Method 2 final result:', { submitLink });
      } else if (!submitLink) {
        // console.log('[CleanAnswerOptions] Method 2: No question._links available');
      }
      
      // Method 3: Construct endpoint from IDs
      if (!submitLink && currentQuestion?.examId && currentQuestion?.id) {
        const constructedLink = `/api/exam/${currentQuestion.examId}/question/${currentQuestion.id}`;
        // console.log('[CleanAnswerOptions] Method 3: Constructing endpoint from IDs:', {
        //   examId: currentQuestion.examId,
        //   questionId: currentQuestion.id,
        //   constructedLink
        // });
        submitLink = constructedLink;
      } else if (!submitLink) {
        // console.log('[CleanAnswerOptions] Method 3: Missing examId or questionId:', {
        //   examId: currentQuestion?.examId,
        //   questionId: currentQuestion?.id
        // });
      }

      // Method 4: Check if there's a response endpoint from the question list
      if (!submitLink && currentQuestion?.response) {
        // console.log('[CleanAnswerOptions] Method 4: Checking response object for links:', {
        //   response: currentQuestion.response,
        //   responseKeys: Object.keys(currentQuestion.response),
        //   responseLinksExists: !!currentQuestion.response._links
        // });
      }

      // console.log('[CleanAnswerOptions] ===== FINAL ENDPOINT DECISION =====');
      // console.log('[CleanAnswerOptions] Final submitLink:', { submitLink });

      if (!submitLink) {
        const errorMsg = 'Cannot submit answer: No save endpoint available';
        // console.error('[CleanAnswerOptions] ERROR:', errorMsg);
        // console.log('[CleanAnswerOptions] Full question object for debugging:', currentQuestion);
        setError(errorMsg);
        return;
      }

      const response = await examApi.followHateoasLink(submitLink, 'POST', payload);
      const apiIsCorrect = response?.IsCorrect !== undefined ? response.IsCorrect : isAnswerCorrect;

      // console.log('[CleanAnswerOptions] API response received:', {
      //   response,
      //   apiIsCorrect,
      //   onAnswerSubmittedExists: !!onAnswerSubmitted,
      //   timestamp: new Date().toISOString()
      // });

      // Update answers with API response data if available
      if (response?.Answers && Array.isArray(response.Answers)) {
        // console.log('[CleanAnswerOptions] Updating answers with API response data');
        setAnswers(prevAnswers => {
          return prevAnswers.map(answer => {
            const apiAnswer = response.Answers.find(apiAns => apiAns.id === answer.id);
            if (apiAnswer) {
              return {
                ...answer,
                isCorrect: apiAnswer.isCorrect,
                explanation: apiAnswer.explanation || answer.explanation
              };
            }
            return answer;
          });
        });
      }      // Notify parent
      // console.log('[CleanAnswerOptions] About to call onAnswerSubmitted callback:', {
      //   onAnswerSubmittedExists: !!onAnswerSubmitted,
      //   apiIsCorrect,
      //   beforeCall: true
      // });
      
      if (onAnswerSubmitted) {
        // Create complete response object for the parent callback
        const responseObject = {
          questionId: currentQuestion?.id,
          isCorrect: apiIsCorrect,
          answerIds: [selectedAnswerObj.id], // Pass the actual answer ID
          timeSpent: timeSpent,
          isFlagged: isFlagged || false,
          timestamp: new Date().toISOString()
        };
        
        // console.log('[CleanAnswerOptions] CALLING onAnswerSubmitted with complete response object:', responseObject);
        onAnswerSubmitted(responseObject);
        // console.log('[CleanAnswerOptions] onAnswerSubmitted callback completed');
      } else {
        // console.warn('[CleanAnswerOptions] onAnswerSubmitted callback not available!');
      }

      // Navigation is now handled by the Next button in PracticeTest
    } catch (err) {
      // console.error('Error submitting answer:', err);
      setError(`Failed to submit answer: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };  // Skip question
  const skipQuestion = async () => {
    if (isAnswerSubmitted || submitting || isLocked) return;

    // console.log('[CleanAnswerOptions] Skip question triggered - calling parent skip handler');
    
    // Use the new skip handler that just navigates without marking as answered
    if (onSkipQuestion) {
      onSkipQuestion();
    } else {
      // console.warn('[CleanAnswerOptions] No onSkipQuestion handler provided, falling back to navigation');
      if (onNavigateNext) {
        onNavigateNext();
      }
    }
  };

  // Utility functions
  const getAnswerLetter = (idx) => String.fromCharCode(65 + idx);
  
  // Determine if this question actually has a user answer recorded
  // In review mode, we only want to show status if the user answered
  const hasUserAnswer = Boolean(
    currentQuestion?.response?.answerIds && currentQuestion.response.answerIds.length > 0
  );

  // Show status only after submission, or in review mode when an answer exists
  const showAnswerStatus = Boolean(
    isAnswerSubmitted || (reviewMode && hasUserAnswer)
  );
  
  const getExplanation = (answerObj) => {
    if (!answerObj || typeof answerObj === 'string') return '';
    return answerObj.explanation || answerObj.feedback || answerObj.explanationFormatted || '';
  };

  const hasExplanation = (answerObj) => {
    const explanation = getExplanation(answerObj);
    return explanation && explanation.trim().length > 0;
  };
  const toggleExplanation = (idx) => {
    // console.log('[CleanAnswerOptions] toggleExplanation called for idx:', idx, 'current state:', explanationVisibility[idx]);
    setExplanationVisibility(prev => {
      const newState = {
        ...prev,
        [idx]: !prev[idx]
      };
      // console.log('[CleanAnswerOptions] New explanation visibility state:', newState);
      return newState;
    });
  };
  const toggleInsight = (idx) => {
    setInsightVisibility(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };
  const buildOptionClasses = (ans, answerLetter) => {
    const isSelected = selectedAnswer === answerLetter;
    const isCorrectAnswer = ans.isCorrect === true;
    const isSelectedAnswer = isSelected;
    
    const conditions = {
      selected: isSelected && !isAnswerSubmitted,
      correct: isAnswerSubmitted && isCorrectAnswer,
      incorrect: isAnswerSubmitted && isSelected && !isCorrectAnswer,
      locked: isAnswerSubmitted || reviewMode // Add locked state when answer is submitted or in review
    };
    
    const classes = clsx('option', conditions);
    
    // console.log('[CleanAnswerOptions] Building option classes - DETAILED:', {
    //   answerLetter,
    //   selectedAnswer,
    //   isAnswerSubmitted,
    //   answerObject: {
    //     id: ans.id,
    //     isCorrect: ans.isCorrect,
    //     text: (ans.body || ans.bodyFormatted || '').substring(0, 30) + '...'
    //   },
    //   calculatedValues: {
    //     isSelected,
    //     isCorrectAnswer,
    //     isSelectedAnswer
    //   },
    //   conditions,
    //   finalClasses: classes,
    //   rawClassString: classes.toString(),
    //   shouldShowCorrect: conditions.correct,
    //   shouldShowIncorrect: conditions.incorrect,
    //   shouldShowSelected: conditions.selected,
    //   isLocked: conditions.locked
    // });
    
    return classes;
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        padding: '40px',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <FontAwesomeIcon 
          icon={faSpinner} 
          spin 
          style={{ 
            fontSize: '24px', 
            color: themeStyles.text.secondary 
          }} 
        />
        <div style={{ color: themeStyles.text.secondary }}>Loading answer options...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center',
        color: '#dc2626',
        backgroundColor: isDarkMode ? 'rgba(220, 38, 38, 0.1)' : 'rgba(220, 38, 38, 0.05)',
        borderRadius: '8px',
        border: '1px solid rgba(220, 38, 38, 0.2)'
      }}>
        <div style={{ marginBottom: '15px' }}>{error}</div>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '8px 16px',
            backgroundColor: isDarkMode ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.1)',
            color: isDarkMode ? '#3b82f6' : '#2563eb',
            border: '1px solid rgba(59,130,246,0.3)',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Refresh Page
        </button>
      </div>
    );
  }

  // No answers state
  if (!answers || answers.length === 0) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        color: themeStyles.text.secondary 
      }}>
        No answer options available for this question.
      </div>
    );
  }

  return (
    <div className="answer-options-container" style={{ 
      display: 'flex', 
      flexDirection: 'column',
  // Do not grow; otherwise it adds empty space before the bottom controls
  flex: '0 0 auto'
    }}>      {/* Answer Options using CSS classes */}
      <div className="options">
        {/* {console.log('[CleanAnswerOptions] RENDERING OPTIONS - Current state:', {
          isAnswerSubmitted,
          selectedAnswer,
          isCorrect,
          answersCount: answers.length,
          timestamp: new Date().toISOString(),
          answers: answers.map((ans, idx) => ({
            index: idx,
            letter: String.fromCharCode(65 + idx),
            id: ans.id,
            isCorrect: ans.isCorrect,
            text: (ans.body || ans.bodyFormatted || '').substring(0, 20) + '...'
          }))
        })} */}
          {/* Show "Previously Answered" indicator when question was already answered */}
  {(isAnswerSubmitted || (reviewMode && hasUserAnswer)) && (
          <div style={{
            backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: '500',
            color: isDarkMode ? '#60a5fa' : '#2563eb'
          }}>
            📝 This question has been previously answered • Read-only mode
          </div>
        )}
        
        {/* Show "Question Locked" indicator when question is locked */}
        {isLocked && !isAnswerSubmitted && (
          <div style={{
            backgroundColor: isDarkMode ? 'rgba(156, 163, 175, 0.15)' : 'rgba(156, 163, 175, 0.1)',
            border: '1px solid rgba(156, 163, 175, 0.3)',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: '500',
            color: isDarkMode ? '#9ca3af' : '#6b7280'
          }}>
            🔒 Question locked • Already answered or skipped
          </div>
        )}
        
        {answers.map((ans, idx) => {
          const answerLetter = getAnswerLetter(idx);
          const isSelected = selectedAnswer === answerLetter;
          const optionClasses = buildOptionClasses(ans, answerLetter);

          // console.log('[CleanAnswerOptions] Rendering individual option:', {
          //   index: idx,
          //   answerLetter,
          //   isSelected,
          //   optionClasses,
          //   answerIsCorrect: ans.isCorrect,
          //   isAnswerSubmitted,
          //   hasExplanation: hasExplanation(ans),
          //   explanationVisible: explanationVisibility[idx],
          //   DOM_elementWillHaveClasses: optionClasses.split(' ').filter(c => c.trim())
          // });          
          return (            <div
              key={`answer-${idx}`}
              className={optionClasses}
              onClick={(e) => {
                // Only handle option selection if not submitted and not locked
                // Check if the click came from the explanation icon
                if (e.target.closest('.explanation-icon')) {
                  // console.log('[CleanAnswerOptions] Click came from explanation icon, ignoring option click');
                  return;
                }
                if (!isAnswerSubmitted && !isLocked) {
                  handleAnswerSelect(answerLetter);
                }
              }}
              style={{
                cursor: (isAnswerSubmitted || isLocked) ? 'default' : 'pointer'
              }}
              title={isAnswerSubmitted ? 'This question has already been answered' : ''}
            ><div className="option-content">
                {/* Letter indicator */}
                <div className={clsx(
                  "letter-indicator",
                  isSelected && (isAnswerSubmitted ? ans.isCorrect ? "correct" : "incorrect" : "selected")
                )}>
                  {answerLetter}
                </div>

                {/* Answer text with proper CSS class styling */}
                <div className={clsx(
                  "answer-text",
                  isSelected && "selected"
                )}>
                  {ans.bodyFormatted || ans.body || ans.text || ans.answer || ''}
                </div>
                  {/* Explanation toggle icon - positioned at far right of answer line */}
                {(isAnswerSubmitted || reviewMode) && hasExplanation(ans) && !(ans.isCorrect || isSelected) && (
                  <div 
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      // console.log('[CleanAnswerOptions] Explanation icon clicked for answer:', idx, answerLetter);
                      toggleExplanation(idx);
                    }}
                    className="explanation-icon"
                    title={explanationVisibility[idx] ? 'Hide explanation' : 'Show explanation'}
                    style={{
                      pointerEvents: 'all', // Ensure this element can receive clicks
                      zIndex: 10 // Make sure it's above other elements
                    }}
                  >
                    <FontAwesomeIcon
                      icon={explanationVisibility[idx] ? faAngleUp : faCircleInfo}
                      style={{
                        fontSize: '16px',
                        pointerEvents: 'none' // Let clicks pass through to parent
                      }}
                    />
                  </div>
                )}

                {/* NBME Insight toggle — shown for ALL answers in review, only when insight content exists */}
                {(isAnswerSubmitted || reviewMode) && hasInsight(ans) && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleInsight(idx);
                    }}
                    className="explanation-icon insight-icon"
                    title={insightVisibility[idx] ? 'Hide NBME Insight' : 'Show NBME Insight'}
                    style={{
                      pointerEvents: 'all',
                      zIndex: 10,
                      marginLeft: '8px',
                      color: insightVisibility[idx]
                        ? (isDarkMode ? '#fcd34d' : '#b45309')
                        : (isDarkMode ? '#fbbf24' : '#d97706')
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faLightbulb}
                      style={{
                        fontSize: '16px',
                        pointerEvents: 'none'
                      }}
                    />
                  </div>
                )}

                {/* Status indicator for submitted answers */}
                {(isAnswerSubmitted || reviewMode) && (ans.isCorrect || isSelected) && (
                  <div className={clsx(
                    "status-indicator",
                    ans.isCorrect ? "correct" : "incorrect"
                  )}>
                    {ans.isCorrect ? '✓' : '✗'}
                  </div>
                )}
              </div>

              {/* Answer assets - images/media displayed below the answer text */}
              {ans.assets && ans.assets.length > 0 && (
                <div style={{ width: '100%', marginTop: '8px', marginLeft: '40px' }}>
                  <AssetDisplay 
                    assets={ans.assets}
                    isDarkMode={isDarkMode}
                    themeStyles={themeStyles}
                    compact={true}
                  />
                </div>
              )}

              {/* Explanation section */}
              {(isAnswerSubmitted || reviewMode) && hasExplanation(ans) && (
                <div style={{ marginTop: '8px' }}> {/* Reduced from 12px to 8px */}
                  {/* Auto-show explanation for correct answers or selected incorrect answers */}
                  {(ans.isCorrect || isSelected) ? (
                    <div className={clsx(
                      "explanation-content",
                      ans.isCorrect ? "correct" : "incorrect"
                    )}>
                      <div style={{ 
                        fontWeight: 'bold', 
                        marginBottom: '4px', /* Reduced from 6px to 4px */
                        color: ans.isCorrect ? '#16a34a' : '#dc2626'
                      }}>
                        {ans.isCorrect ? 'Correct:' : 'Explanation:'}
                      </div>
                      <div>{getExplanation(ans)}</div>
                      {/* Explanation assets - shown only after answer is submitted */}
                      {ans.explanationAssets && ans.explanationAssets.length > 0 && (
                        <AssetDisplay 
                          assets={ans.explanationAssets}
                          isDarkMode={isDarkMode}
                          themeStyles={themeStyles}
                          compact={true}
                        />
                      )}
                    </div>
                  ) : (
                    /* Show explanation content when toggled for other options */
                    explanationVisibility[idx] && (
                      <div className={clsx(
                        "explanation-content",
                        ans.isCorrect ? "correct" : "incorrect"
                      )}>
                        <div style={{ 
                          fontWeight: 'bold', 
                          marginBottom: '6px',
                          color: ans.isCorrect ? '#16a34a' : '#dc2626'
                        }}>
                          Explanation:
                        </div>
                        <div>{getExplanation(ans)}</div>
                        {/* Explanation assets - shown only after answer is submitted */}
                        {ans.explanationAssets && ans.explanationAssets.length > 0 && (
                          <AssetDisplay 
                            assets={ans.explanationAssets}
                            isDarkMode={isDarkMode}
                            themeStyles={themeStyles}
                            compact={true}
                          />
                        )}
                      </div>
                    )
                  )}
                </div>
              )}

              {/* NBME Insight panel — rendered below the explanation section when toggled open */}
              {(isAnswerSubmitted || reviewMode) && hasInsight(ans) && insightVisibility[idx] && (
                <InsightPanel
                  insightsHtml={ans.insights}
                  isDarkMode={isDarkMode}
                  themeStyles={themeStyles}
                />
              )}
            </div>
          );        })}
      </div>      {/* Submit and Skip buttons - only show if not hidden by parent */}
      {!hideButtons && !isAnswerSubmitted && !isLocked && (
        <div className="answer-actions" style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          marginTop: '20px',
          padding: '16px 0',
          borderTop: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.05)'
        }}>          {/* Skip button */}
          <button
            onClick={skipQuestion}
            disabled={submitting || isLocked}
            style={{
              padding: '10px 16px',
              borderRadius: '6px',
              backgroundColor: isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.1)',
              color: isDarkMode ? '#94a3b8' : '#64748b',
              border: 'none',
              cursor: (submitting || isLocked) ? 'not-allowed' : 'pointer',
              opacity: (submitting || isLocked) ? 0.5 : 1,
              transition: 'all 0.2s ease',
              fontWeight: 500
            }}
          >
            {submitting ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
                Skipping...
              </>
            ) : (
              'Skip'
            )}
          </button>

          {/* Submit button */}
          <button
            onClick={submitAnswer}
            disabled={!selectedAnswer || submitting || isLocked}
            style={{
              padding: '10px 20px',
              borderRadius: '6px',
              backgroundColor: (!selectedAnswer || submitting || isLocked) ? 
                (isDarkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.1)') : 
                (isDarkMode ? '#3b82f6' : '#3b82f6'),
              color: (!selectedAnswer || submitting || isLocked) ? 
                (isDarkMode ? '#64748b' : '#94a3b8') : 
                '#ffffff',
              border: 'none',
              cursor: (!selectedAnswer || submitting || isLocked) ? 'not-allowed' : 'pointer',
              opacity: (!selectedAnswer || submitting || isLocked) ? 0.5 : 1,
              transition: 'all 0.2s ease',
              fontWeight: 600
            }}
          >
            {submitting ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
                Submitting...
              </>
            ) : (
              'Submit Answer'
            )}
          </button>
        </div>
      )}

      {/* Status display when answered */}
      {/* {console.log('[CleanAnswerOptions] Checking if answer status should render:', {
        isAnswerSubmitted,
        isCorrect,
        shouldRender: isAnswerSubmitted,
        statusClasses: isAnswerSubmitted ? clsx("answer-status", isCorrect ? "correct" : "incorrect") : 'N/A'
      })} */}
      
  {showAnswerStatus && (
        <div className={clsx(
          "answer-status",
          isCorrect ? "correct" : "incorrect"
        )}>
          {/* {console.log('[CleanAnswerOptions] Rendering answer status:', {
            isCorrect,
            statusClasses: clsx("answer-status", isCorrect ? "correct" : "incorrect"),
            statusText: isCorrect ? 'Correct Answer!' : 'Incorrect Answer'
          })} */}
          <FontAwesomeIcon 
            icon={isCorrect ? faCheckCircle : faTimesCircle} 
            className="status-icon"
          />
          {isCorrect ? 'Correct Answer!' : 'Incorrect Answer'}
        </div>
      )}
    </div>
  );
});

export default CleanAnswerOptions;
